/**
 * The models the Sidekick can run on, and the one list the panel's picker reads.
 *
 * The options themselves live in `sidekick_models.json` at the repo root, which
 * the Go backend embeds too — ONE list, so the picker and the allowlist that
 * validates its choices cannot disagree. This file is just the typed view of it
 * plus the resolution rules.
 *
 * Per-user, not per-bot: the Sidekick runs against the shared `voxcore-helper`
 * system bot, so this cannot live in a bot config without changing the model for
 * everyone on the platform.
 */
import registry from '../../../../sidekick_models.json';

/** Mirrors `SidekickModel` in sidekick_model.go — both read the same JSON. */
export interface SidekickModelOption {
  /** Wire value + preference value. Stable — renaming resets users' choices. */
  id: string;
  /** Dropdown label. */
  label: string;
  /** One short line under the label. Say what the trade is, not what it is. */
  hint: string;
  /**
   * Hidden from non-platform-admins. The Opus options are ~50x the token cost of
   * Flash Lite and noticeably slower to first token, which is a fine trade for
   * evaluating setup quality and a bad default to hand every customer.
   */
  adminOnly?: boolean;
  /** Backend-only fields, carried so one file describes the whole option. */
  provider?: string;
  model?: string;
  effort?: string;
  adaptiveThinking?: boolean;
}

export const SIDEKICK_MODELS: SidekickModelOption[] = registry as SidekickModelOption[];

/** Matches DefaultSidekickModelID in sidekick_model.go. */
export const DEFAULT_SIDEKICK_MODEL_ID = 'gemini-3.1-flash-lite';

export function sidekickModelIDs(): string[] {
  return SIDEKICK_MODELS.map((m) => m.id);
}

/** Options this viewer may pick from. */
export function visibleSidekickModels(isPlatformAdmin: boolean): SidekickModelOption[] {
  return SIDEKICK_MODELS.filter((m) => !m.adminOnly || isPlatformAdmin);
}

/**
 * Resolve a stored preference to the id to actually send.
 *
 * Returns the default for an unknown id (an option we retired) and for an
 * admin-only id held by someone who is no longer an admin — in both cases the
 * alternative is sending a value the picker cannot display, so the panel would
 * show one model while running another.
 */
export function resolveSidekickModelID(
  stored: string | undefined,
  isPlatformAdmin: boolean,
): string {
  const found = SIDEKICK_MODELS.find((m) => m.id === stored);
  if (!found) return DEFAULT_SIDEKICK_MODEL_ID;
  if (found.adminOnly && !isPlatformAdmin) return DEFAULT_SIDEKICK_MODEL_ID;
  return found.id;
}
